/**
 * ChatProvider - Global chat state management
 * Provides chat functionality throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/hooks/useAuth';
import { chatApi } from '../services/chatApi';
import { Conversation, Message, ChatContextValue } from '../types/chat';
import { useNotificationsSummary } from '../../notifications/hooks/useNotificationsSummary';

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, sessionEstablishing } = useAuth();
  const queryClient = useQueryClient();
  
  // Use notifications summary for unread count (replaces separate unread count polling)
  const { summary: notificationsSummary } = useNotificationsSummary();
  
  // Track if conversations should be polled (only when chat page is open OR chat dock is expanded)
  // Use a refcount so the full chat page and persistent dock cannot disable each other's polling.
  const [activeChatSurfaceCount, setActiveChatSurfaceCount] = useState<number>(0);
  const shouldPollConversations = activeChatSurfaceCount > 0;
  
  // Phase 3: Use React Query for conversations with SWR
  // Include user ID in query key to ensure separate cache per user
  const userId = session?.user?.id;
  
  // Track the userId that the query data belongs to
  // This prevents showing data from a different user when userId changes
  const queryUserIdRef = useRef<string | null>(null);
  
  const { 
    data: conversations = [], 
    isLoading: conversationsLoading,
    isFetching: conversationsFetching,
    error: conversationsError
  } = useQuery<Conversation[]>({
    queryKey: ['conversations', userId],
    queryFn: () => {
      // Update the ref to track which user this data belongs to
      queryUserIdRef.current = userId || null;
      return chatApi.getConversations(session!.access_token);
    },
    enabled: !!session?.access_token && !!userId && !sessionEstablishing && shouldPollConversations,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    refetchInterval: () => {
      // Only poll if chat is active and tab is visible
      if (!shouldPollConversations || document.hidden) {
        return false;
      }
      // Fixed 60s interval (React Query's retry mechanism handles error backoff)
      return 60000; // 60 seconds
    },
    refetchIntervalInBackground: false, // Only poll when tab is visible
    refetchOnWindowFocus: true,
    retry: (failureCount) => {
      // Exponential backoff: retry up to 3 times
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    // SWR: Keep previous data while refetching, but only for the same user
    // CRITICAL: Check if previousData belongs to the current user to prevent data leakage
    placeholderData: (previousData) => {
      // Only keep previous data if:
      // 1. We have previousData
      // 2. Current userId exists
      // 3. The previousData belongs to the current user (checked via ref)
      if (previousData && userId && queryUserIdRef.current === userId) {
        return previousData;
      }
      // Return undefined to show loading state when no valid cached data or user changed
      return undefined;
    },
  });

  // State
  const [messagesByConversationId, setMessagesByConversationId] = useState<Record<string, Message[]>>({});
  const [draftsByConversationId, setDraftsByConversationId] = useState<Record<string, string>>({});
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null); // Local error for actions (send, start chat, etc.)

  // Derive unreadTotal from notifications summary
  const unreadTotal = notificationsSummary.unreadConversationCount;
  
  // Loading and error states
  // Show loading when: initial load OR fetching with no cached data
  const isLoading = conversationsLoading || (conversationsFetching && conversations.length === 0);
  const error = conversationsError ? 'Failed to load conversations' : actionError;

  // Polling intervals for messages (still needed for active conversation)
  const messagesIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef<boolean>(true);
  
  // Track last fetch timestamps for debouncing
  const lastMessagesFetchRef = useRef<number>(0);
  
  // Error backoff state (consecutive failures)
  const messagesErrorCountRef = useRef<number>(0);

  // Track previous user ID to detect user changes
  const previousUserIdRef = useRef<string | null>(null);

  /**
   * Clear all chat state when user logs out or changes
   * This prevents data leakage between different user accounts
   */
  useEffect(() => {
    const currentUserId = session?.user?.id ?? null;
    const previousUserId = previousUserIdRef.current;

    // Clear state if:
    // 1. Session is null (user logged out)
    // 2. User ID changed (different user logged in)
    if (!session || !currentUserId || (previousUserId !== null && previousUserId !== currentUserId)) {
      // Clear all local chat state
      setMessagesByConversationId({});
      setActiveConversationId(null);
      setLoadingConversationId(null);
      setIsSendingMessage(false);
      setActionError(null);
      setDraftsByConversationId({});
      
      // Clear polling state and refs
      setActiveChatSurfaceCount(0);
      messagesErrorCountRef.current = 0;
      lastMessagesFetchRef.current = 0;
      
      // Clear message polling interval
      if (messagesIntervalRef.current) {
        clearInterval(messagesIntervalRef.current);
        messagesIntervalRef.current = null;
      }

      // Remove ALL conversations queries from React Query cache (for all users)
      // This ensures no data leaks between user accounts
      queryClient.removeQueries({ queryKey: ['conversations'] });
      
      // Also remove any queries for the previous user specifically
      if (previousUserId) {
        queryClient.removeQueries({ queryKey: ['conversations', previousUserId] });
      }
      
      // CRITICAL: Reset the query data for the current user to empty array
      // This prevents placeholderData from returning old data from a different user
      if (currentUserId) {
        queryClient.setQueryData(['conversations', currentUserId], []);
        // Reset the ref to null so placeholderData won't return old data
        queryUserIdRef.current = null;
      } else {
        // If no current user, reset ref
        queryUserIdRef.current = null;
      }
    }

    // Update previous user ID
    previousUserIdRef.current = currentUserId;
  }, [session, session?.user?.id, queryClient]);

  // Update queryUserIdRef when userId changes (for placeholderData check)
  useEffect(() => {
    // Reset ref when userId changes to prevent showing old user's data
    if (userId !== queryUserIdRef.current) {
      queryUserIdRef.current = null;
    }
  }, [userId]);

  /**
   * Load all conversations - now handled by React Query
   * Note: React Query auto-fetches when enabled, so this is mainly for manual refresh
   */
  const loadConversations = useCallback(async () => {
    // React Query will auto-fetch when enabled=true, so we don't need to force it
    // This function is kept for backward compatibility and manual refresh scenarios
    // Invalidate to trigger a refetch (React Query will dedupe if already fetching)
    // Include userId in query key to target the correct user's conversations
    if (userId) {
      await queryClient.invalidateQueries({ queryKey: ['conversations', userId] });
    }
  }, [queryClient, userId]);

  /**
   * Refresh conversations (silent, no loading state) - now handled by React Query
   */
  const refreshConversations = useCallback(async () => {
    // Invalidate query to trigger background refetch (uses cached data immediately)
    // Include userId in query key to target the correct user's conversations
    if (userId) {
      await queryClient.invalidateQueries({ queryKey: ['conversations', userId] });
    }
  }, [queryClient, userId]);

  /**
   * Refresh unread count - now handled by useNotificationsSummary hook
   * This function is kept for backward compatibility but does nothing
   */
  const refreshUnreadCount = useCallback(async () => {
    // Unread count is now managed by useNotificationsSummary hook
    // This is a no-op for backward compatibility
  }, []);

  const setConversationDraft = useCallback((conversationId: string, body: string) => {
    setDraftsByConversationId((prev) => {
      if (!body) {
        const { [conversationId]: _removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [conversationId]: body,
      };
    });
  }, []);

  const clearConversationDraft = useCallback((conversationId: string) => {
    setDraftsByConversationId((prev) => {
      const { [conversationId]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Mark conversation as read
   */
  const markRead = useCallback(
    async (conversationId: string) => {
      if (!session?.access_token || sessionEstablishing || !userId) return;

      try {
        await chatApi.markAsRead(session.access_token, conversationId);
        
        // Invalidate queries to refresh data
        // Backend invalidates Redis cache, so refetch will get fresh data with updated unreadCount
        queryClient.invalidateQueries({ queryKey: ['conversations', userId] });
        queryClient.invalidateQueries({ queryKey: ['notificationsSummary'] });
      } catch (err) {
        console.error('Failed to mark as read:', err);
        // On error, still invalidate to ensure UI reflects current state
        queryClient.invalidateQueries({ queryKey: ['conversations', userId] });
      }
    },
    [queryClient, userId, session?.access_token, sessionEstablishing]
  );

  /**
   * Open a conversation and load its messages
   */
  const openConversation = useCallback(
    async (conversationId: string) => {
      if (!session?.access_token || sessionEstablishing) return;

      setActiveConversationId(conversationId);

      // If messages already cached, don't reload
      if (messagesByConversationId[conversationId]) {
        // Mark as read (fire and forget - don't await)
        void markRead(conversationId);
        return;
      }

      // Set loading state for this conversation
      setLoadingConversationId(conversationId);

      try {
        const result = await chatApi.getMessages(session.access_token, conversationId, {
          limit: 50,
        });
        
        setMessagesByConversationId((prev) => ({
          ...prev,
          [conversationId]: result.messages,
        }));

        // Mark as read (fire and forget - don't await)
        void markRead(conversationId);
        setActionError(null);
      } catch (err) {
        console.error('Failed to load messages:', err);
        // Preserve existing messages for this conversation if they exist
        setActionError('Failed to load messages');
        // Don't clear messagesByConversationId[conversationId] on error
      } finally {
        // Clear loading state
        setLoadingConversationId(null);
      }
    },
    [session?.access_token, sessionEstablishing, messagesByConversationId, markRead]
  );

  /**
   * Start a new chat with a friend
   */
  const startNewChat = useCallback(
    async (friendUserId: string) => {
      if (!session?.access_token || sessionEstablishing) return;

      try {
        const conversation = await chatApi.createConversation(session.access_token, friendUserId);
        
        // Invalidate conversations query to refetch (will include new conversation)
        // Include userId in query key to target the correct user's conversations
        if (userId) {
          await queryClient.invalidateQueries({ queryKey: ['conversations', userId] });
        }

        // Open the conversation
        await openConversation(conversation.id);
        setActionError(null);
      } catch (err: any) {
        console.error('Failed to start chat:', err);
        setActionError(err.message || 'Failed to start chat');
        throw err;
      }
    },
    [session?.access_token, sessionEstablishing, openConversation, queryClient, userId]
  );

  /**
   * Send a message
   */
  const sendMessage = useCallback(
    async (conversationId: string, body: string) => {
      if (!session?.access_token || sessionEstablishing) return;

      const previousDraft = draftsByConversationId[conversationId] ?? '';

      try {
        setIsSendingMessage(true);

        // Optimistic UI: Add message immediately
        const tempId = `temp-${Date.now()}`;
        const tempMessage: Message = {
          id: tempId,
          conversationId,
          senderId: session.user?.id || '',
          senderName: 'You',
          body,
          createdAt: new Date().toISOString(),
          isMine: true,
        };

        setMessagesByConversationId((prev) => ({
          ...prev,
          [conversationId]: [...(prev[conversationId] || []), tempMessage],
        }));
        clearConversationDraft(conversationId);

        // Send to server
        const message = await chatApi.sendMessage(session.access_token, conversationId, body);

        // Replace temp message with real one
        setMessagesByConversationId((prev) => {
          const messages = prev[conversationId] || [];
          const index = messages.findIndex((m) => m.id === tempId);
          if (index === -1) return prev;

          const newMessages = [...messages];
          newMessages[index] = message;
          return {
            ...prev,
            [conversationId]: newMessages,
          };
        });

        // Refresh conversations to update last message
        await refreshConversations();
        
        // Mark conversation as read (so our own message doesn't show as unread)
        // Fire and forget - don't await
        void markRead(conversationId);
        
        setActionError(null);
      } catch (err: any) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        console.error('Failed to send message:', err);
        setActionError(errorMessage);
        
        // Remove temp message on error
        setMessagesByConversationId((prev) => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).filter(
            (m) => !m.id.startsWith('temp-')
          ),
        }));
        if (previousDraft) {
          setConversationDraft(conversationId, previousDraft);
        }
        
        // Don't re-throw - error is already handled and displayed via actionError
        // Re-throwing causes unhandled promise rejection warnings
      } finally {
        setIsSendingMessage(false);
      }
    },
    [
      session?.access_token,
      session?.user?.id,
      sessionEstablishing,
      draftsByConversationId,
      clearConversationDraft,
      setConversationDraft,
      refreshConversations,
      markRead,
    ]
  );

  /**
   * Load more messages (pagination)
   */
  const loadMoreMessages = useCallback(
    async (conversationId: string) => {
      if (!session?.access_token || sessionEstablishing) return;

      const currentMessages = messagesByConversationId[conversationId] || [];
      if (currentMessages.length === 0) return;

      const oldestMessage = currentMessages[0];

      try {
        const result = await chatApi.getMessages(session.access_token, conversationId, {
          limit: 50,
          before: oldestMessage.createdAt,
        });

        if (result.messages.length > 0) {
          setMessagesByConversationId((prev) => ({
            ...prev,
            [conversationId]: [...result.messages, ...currentMessages],
          }));
        }
      } catch (err) {
        console.error('Failed to load more messages:', err);
      }
    },
    [session?.access_token, sessionEstablishing, messagesByConversationId]
  );

  const registerChatSurface = useCallback(() => {
    setActiveChatSurfaceCount((count) => count + 1);
  }, []);

  const unregisterChatSurface = useCallback(() => {
    setActiveChatSurfaceCount((count) => Math.max(0, count - 1));
  }, []);

  // Polling for conversations is now handled by React Query's refetchInterval
  // The query is enabled/disabled based on whether at least one chat surface is active.

  // Polling for active conversation messages (every 20 seconds when visible)
  // Only poll when chat is active (chat page open or chat dock expanded)
  useEffect(() => {
    if (!session?.access_token || sessionEstablishing || !activeConversationId || !shouldPollConversations) return;

    const getPollInterval = () => {
      // Error backoff: increase interval on consecutive failures
      const baseInterval = 20000; // 20 seconds
      const errorCount = messagesErrorCountRef.current;
      if (errorCount === 0) return baseInterval;
      if (errorCount === 1) return baseInterval * 2; // 40s
      if (errorCount === 2) return baseInterval * 4; // 80s
      return Math.min(baseInterval * 5, 300000); // 300s max (5 minutes)
    };

    const pollMessages = async () => {
      // Re-check session validity on each poll to prevent 401 errors
      if (!session?.access_token || sessionEstablishing || !activeConversationId) return;
      
      // Only poll if app is actually active (tab visible + window focused)
      if (!isVisibleRef.current) return;
      
      // Don't poll if messages are currently being loaded (avoid duplicate fetches)
      if (loadingConversationId === activeConversationId) return;
      
      try {
        const result = await chatApi.getMessages(session.access_token, activeConversationId, {
          limit: 50,
        });
        setMessagesByConversationId((prev) => ({
          ...prev,
          [activeConversationId]: result.messages,
        }));
        messagesErrorCountRef.current = 0; // Reset error count on success
        lastMessagesFetchRef.current = Date.now();
      } catch (err) {
        // Silently handle auth errors to avoid console spam
        const errorMessage = err instanceof Error ? err.message : String(err);
        if (!errorMessage.includes('UNAUTHORIZED') && !errorMessage.includes('401')) {
          console.error('Failed to poll messages:', err);
        }
        // Preserve existing messages on error
        messagesErrorCountRef.current += 1;
      }
    };

    // Don't poll immediately - openConversation already fetches messages
    // Just set up the interval for future polling
    const interval = getPollInterval();
    messagesIntervalRef.current = setInterval(pollMessages, interval);

    return () => {
      if (messagesIntervalRef.current) {
        clearInterval(messagesIntervalRef.current);
        messagesIntervalRef.current = null;
      }
    };
  }, [session?.access_token, sessionEstablishing, activeConversationId, loadingConversationId, shouldPollConversations]);

  // Unread count polling is now handled by useNotificationsSummary hook
  // No separate polling needed here

  // Track whether the app is actually "active" (tab visible + window focused)
  useEffect(() => {
    const updateVisibility = () => {
      isVisibleRef.current = !document.hidden;
    };

    const handleFocus = () => {
      const wasVisible = isVisibleRef.current;
      isVisibleRef.current = true;
      
      // When window regains focus and tab is visible, refresh data (debounced)
      if (!document.hidden && !wasVisible && session?.access_token) {
        const now = Date.now();
        const DEBOUNCE_MS = 10000; // 10 seconds
        
        // Unread count refresh is handled by useNotificationsSummary hook (React Query)
        // Conversations refresh is handled by React Query's refetchOnWindowFocus
        
        // If there's an active conversation, refresh its messages too (debounced)
        if (activeConversationId && now - lastMessagesFetchRef.current >= DEBOUNCE_MS) {
          chatApi.getMessages(session.access_token, activeConversationId, { limit: 50 })
            .then((result) => {
              setMessagesByConversationId((prev) => ({
                ...prev,
                [activeConversationId]: result.messages,
              }));
              messagesErrorCountRef.current = 0;
              lastMessagesFetchRef.current = Date.now();
            })
            .catch((err) => {
              console.error('Failed to refresh messages on focus:', err);
              messagesErrorCountRef.current += 1;
            });
        }
      }
    };

    const handleBlur = () => {
      // When the browser window loses focus (you Alt+Tab to another app),
      // stop treating this as "active"
      isVisibleRef.current = false;
    };

    // Initialize visibility state
    isVisibleRef.current = !document.hidden;

    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [session?.access_token, refreshConversations, refreshUnreadCount, activeConversationId]);

  const value: ChatContextValue = {
    conversations,
    messagesByConversationId,
    draftsByConversationId,
    activeConversationId,
    unreadTotal,
    isLoading,
    loadingConversationId,
    isSendingMessage,
    error,
    loadConversations,
    openConversation,
    startNewChat,
    sendMessage,
    markRead,
    loadMoreMessages,
    setConversationDraft,
    clearConversationDraft,
    refreshConversations,
    refreshUnreadCount,
    registerChatSurface,
    unregisterChatSurface,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

/**
 * Hook to access chat context
 */
export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export const useConversationDraft = (conversationId: string | null) => {
  const {
    draftsByConversationId,
    setConversationDraft,
    clearConversationDraft,
  } = useChat();

  const draft = conversationId ? draftsByConversationId[conversationId] ?? '' : '';

  const setDraft = useCallback(
    (body: string) => {
      if (!conversationId) return;
      setConversationDraft(conversationId, body);
    },
    [conversationId, setConversationDraft]
  );

  const clearDraft = useCallback(() => {
    if (!conversationId) return;
    clearConversationDraft(conversationId);
  }, [conversationId, clearConversationDraft]);

  return { draft, setDraft, clearDraft };
};
